/**
 * Звуки интерфейса для всего окна одним слушателем: «тик» при наведении
 * на кнопку и «клик» при нажатии. Делегирование на `document`, а не
 * обработчики в каждой кнопке, — новые экраны звучат без доработок.
 *
 * Наведение — только для мыши (на сенсоре «наведение» = нажатие) и не
 * чаще раза в 70 мс: пробег курсором по сетке карточек не трещит.
 * Отключённые кнопки молчат.
 */

import { useEffect } from 'react';

import { playClick, playHover } from '../services/sound';

const INTERACTIVE = 'button:not(:disabled), a[href], [role="button"], [role="tab"]';
const HOVER_GAP_MS = 70;

export function useUiSounds(): void {
  useEffect(() => {
    let lastTarget: Element | null = null;
    let lastHoverAt = 0;

    const onOver = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
      const target = event.target.closest(INTERACTIVE);
      if (target === lastTarget) return;
      lastTarget = target;
      if (target === null || event.timeStamp - lastHoverAt < HOVER_GAP_MS) return;
      lastHoverAt = event.timeStamp;
      playHover();
    };
    const onDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest(INTERACTIVE) !== null) playClick();
    };
    // Enter/пробел на кнопке — тоже нажатие.
    const onKey = (event: KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && !event.repeat && document.activeElement?.matches(INTERACTIVE) === true) playClick();
    };

    document.addEventListener('pointerover', onOver, { passive: true });
    document.addEventListener('pointerdown', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
}
